import React, { useState } from "react";
import { showToast } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import "./addsalelead.scss";
import {
  fieldValidation,
  addSalesLead,
  buttonName,
  alertActions
} from "../../locales/en";
import {
  COMMISSION_RATE_REGEX,
  NAME_REGEX,
  NAME_REGEX_OPTIONAL,
  showToastType
} from "../../utils/constants/appConstants";
import privateAxios from "../../utils/axiosPrivate";
import { getSalesLeadsURL } from "../../utils/constants/apiEndPoints";
import { SALES_LEADS } from "../../route/paths";

const initialSaleUserState = {
  name: "",
  nickName: "",
  email: "",
  phone: "",
  isDirectClientCommission: false,
  highTouchCommissionRate: "",
  lowTouchCommissionRate: "",
  isReferralCommission: false,
  referralCommissionRate: ""
};

const AddSaleLead = () => {
  const [saleUser, setSaleUser] = useState(initialSaleUserState);

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const {
    name,
    email,
    nickName,
    phone,
    highTouchCommissionRate,
    lowTouchCommissionRate,
    referralCommissionRate,
    isDirectClientCommission,
    isReferralCommission
  } = saleUser;

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setSaleUser({ ...saleUser, [name]: newValue });
    setErrors({ ...errors, [name]: "" });
  };

  const validate = () => {
    const tempErrors = {};
    setMessage("");

    if (!name) {
      tempErrors.name = fieldValidation?.required;
    } else if (name.length < 5) {
      tempErrors.name = addSalesLead?.errMsg.item1;
    } else if (!NAME_REGEX.test(name)) {
      tempErrors.name = addSalesLead?.errMsg.item2;
    }

    if (nickName && !NAME_REGEX_OPTIONAL.test(nickName)) {
      tempErrors.nickName = addSalesLead?.errMsg.item3;
    }

    const areFieldsBlank =
      !nickName &&
      !phone &&
      !highTouchCommissionRate &&
      !lowTouchCommissionRate &&
      !referralCommissionRate;

    if (!areFieldsBlank) {
      const commissionRegex = COMMISSION_RATE_REGEX;
      if (
        highTouchCommissionRate &&
        (!commissionRegex.test(highTouchCommissionRate) ||
          parseFloat(highTouchCommissionRate) >= 100)
      ) {
        tempErrors.highTouchCommissionRate = addSalesLead?.errMsg.item6;
      }

      if (
        lowTouchCommissionRate &&
        (!commissionRegex.test(lowTouchCommissionRate) ||
          parseFloat(lowTouchCommissionRate) >= 100)
      ) {
        tempErrors.lowTouchCommissionRate = addSalesLead?.errMsg.item7;
      }

      if (
        referralCommissionRate &&
        (!commissionRegex.test(referralCommissionRate) ||
          parseFloat(referralCommissionRate) >= 100)
      ) {
        tempErrors.referralCommissionRate = addSalesLead?.errMsg.item8;
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setMessage("");
    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const dataToSend = { ...saleUser };

      const response = await privateAxios.post(getSalesLeadsURL, dataToSend);

      setSaleUser(initialSaleUserState);

      showToast(response.data.message, showToastType.success);
      setTimeout(() => {
        navigate(SALES_LEADS);
      }, 1500);
    } catch (error) {
      const validationErrors = {};
      const { status, data } = error.response || {};

      if (status === 400 && Array.isArray(data?.errors)) {
        const errorMessages = data.errors
          .map((errorItem) => {
            if (typeof errorItem === "object") {
              return Object.values(errorItem).flat().join(", ");
            }
            return errorItem;
          })
          .join("; ");
        showToast(errorMessages, showToastType.error);
      }
      if (error.response && error.response.data.errors) {
        const errorData = error.response.data.errors;
        if (errorData.includes(addSalesLead?.errMsg.item9)) {
          validationErrors.phone = addSalesLead?.errMsg.item9;
        }
        if (errorData.includes(addSalesLead?.errMsg.item10)) {
          validationErrors.email = addSalesLead?.errMsg.item10;
        }
        setErrors(validationErrors);
      } else {
        showToast(
          error.message || addSalesLead?.errMsg.item11,
          showToastType.error
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSaleUser(initialSaleUserState);
    setErrors({});
    navigate(SALES_LEADS);
  };

  return (
    <>
      <div className="row mb-2">
        <div className="col-12 sales-page-title-wrap pb-2">
          <div className="box-heading d-flex flex-wrap align-items-center">
            <h2>{addSalesLead?.mainTitle}</h2>
            <p className="mb-0">{addSalesLead?.subH2}</p>
          </div>
        </div>
      </div>
      <div className="row sales-page-details-wrap mb-4">
        <div className="col-12 d-flex flex-wrap align-items-center">
          <div className="box-title py-2">
            <h3>{addSalesLead?.subH2}</h3>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-8 sale-leade-form offset-md-2">
          {message && (
            <div className="alert alert-success mt-2 mb-2">{message}</div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="name">{addSalesLead?.fullName}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Full Name"
                    name="name"
                    id="name"
                    onChange={onInputChange}
                    value={name}
                    aria-describedby="nameError"
                  />
                  {errors.name && (
                    <div id="nameError" className="text-danger small">
                      {errors.name}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="email">{addSalesLead?.mailAddrs}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="Email Address"
                    name="email"
                    id="email"
                    onChange={onInputChange}
                    value={email}
                    aria-describedby="emailError"
                  />
                  {errors.email && (
                    <div id="emailError" className="text-danger small">
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="nickName">{addSalesLead?.nickName}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Nick Name"
                    name="nickName"
                    id="nickName"
                    value={nickName}
                    onChange={onInputChange}
                  />
                  {errors.nickName && (
                    <div className="text-danger small">{errors.nickName}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="phone">{addSalesLead?.number}</label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="tel"
                    placeholder="Contact Number"
                    name="phone"
                    id="phone"
                    value={phone}
                    onChange={onInputChange}
                  />
                  {errors.phone && (
                    <div className="text-danger small">{errors.phone}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="isDirectClientCommission">
                    {addSalesLead?.clientCommison}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="checkbox"
                    name="isDirectClientCommission"
                    id="isDirectClientCommission"
                    checked={isDirectClientCommission}
                    onChange={onInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="highTouchCommissionRate">
                    {addSalesLead?.highTouchComsn}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="High Touch Commission Rate%"
                    name="highTouchCommissionRate"
                    id="highTouchCommissionRate"
                    value={highTouchCommissionRate}
                    onChange={onInputChange}
                  />
                  {errors.highTouchCommissionRate && (
                    <div className="text-danger small">
                      {errors.highTouchCommissionRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="lowTouchCommissionRate">
                    {addSalesLead?.lowTouchComsn}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Low-Touch Commission Rate%"
                    name="lowTouchCommissionRate"
                    id="lowTouchCommissionRate"
                    value={lowTouchCommissionRate}
                    onChange={onInputChange}
                  />
                  {errors.lowTouchCommissionRate && (
                    <div className="text-danger small">
                      {errors.lowTouchCommissionRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="isReferralCommission">
                    {addSalesLead?.referralCommsn}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="checkbox"
                    name="isReferralCommission"
                    id="isReferralCommission"
                    checked={isReferralCommission}
                    onChange={onInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-sm-4">
                <div className="input-title">
                  <label htmlFor="referralCommissionRate">
                    {addSalesLead?.referralCommsnRate}
                  </label>
                </div>
              </div>
              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    name="referralCommissionRate"
                    id="referralCommissionRate"
                    placeholder="Referral Commission Rate%"
                    value={referralCommissionRate}
                    onChange={onInputChange}
                    aria-describedby="referralCommissionRateError"
                  />
                  {errors.referralCommissionRate && (
                    <div
                      id="referralCommissionRateError"
                      className="text-danger small"
                    >
                      {errors.referralCommissionRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-sm-8 offset-sm-4">
                <div className="input-btn-group d-flex">
                  <button
                    type="submit"
                    className="btn btn-primary me-2"
                    disabled={isSubmitting}
                  >
                    {buttonName?.create}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleCancel}
                  >
                    {alertActions?.Cancel}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddSaleLead;
